/**
 * f14.2: the reconciliation queue table (design D8).
 *
 * Client · Element · Month · Source · Baseline (frozen) · Current · Incoming ·
 * Variance · Status · Decided by / at · Actions. Accept and Reject render only
 * on a PENDING row of an editable cycle the server marks `can_decide`; the
 * server refuses any other decision regardless.
 */

import { Check, X } from 'lucide-react';

import type { ReconciliationVariance } from '@/api/client';

import {
  SOURCE_LABELS,
  STATUS_LABELS,
  SUPERSEDE_LABELS,
  formatAmount,
  monthLabel,
  showsActions,
  statusPill,
  varianceClass,
} from './reconciliationFormat';

const WHEN = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function when(iso: string | null): string {
  return iso ? WHEN.format(new Date(iso)) : '';
}

function Outcome({ variance }: { variance: ReconciliationVariance }) {
  if (variance.status.code === 'SUPERSEDED') {
    const reason = variance.supersede_reason ? SUPERSEDE_LABELS[variance.supersede_reason.code] : null;
    return <span className="hint">{reason ? `Superseded: ${reason}` : 'Superseded'} · {when(variance.superseded_at)}</span>;
  }
  if (!variance.decided_by) return <span className="hint">Raised {when(variance.raised_at)}</span>;
  return (
    <span className="rc-decided">
      <b>{variance.decided_by.name}</b>
      <span className="hint">{when(variance.decided_at)}</span>
      {variance.decision_comment && <span className="rc-comment">“{variance.decision_comment}”</span>}
    </span>
  );
}

export function VarianceTable({
  items,
  cycleStateCode,
  loading,
  onDecide,
}: {
  items: readonly ReconciliationVariance[];
  cycleStateCode: string;
  loading: boolean;
  onDecide: (variance: ReconciliationVariance, decision: 'ACCEPT' | 'REJECT') => void;
}) {
  return (
    <div className="rc-table-wrap">
      <table className="data compact rc-table" aria-busy={loading}>
        <thead>
          <tr>
            <th>Client</th>
            <th>Element</th>
            <th>Month</th>
            <th>Source</th>
            <th className="num">Baseline (frozen)</th>
            <th className="num">Current</th>
            <th className="num">Incoming</th>
            <th className="num">Variance</th>
            <th>Status</th>
            <th>Decided by / at</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {items.map((variance) => {
            const currency = variance.currency_code;
            return (
              <tr key={variance.id} data-status={variance.status.code}>
                <td>
                  <div className="rc-client">{variance.client.name}</div>
                  <div className="hint">{variance.business_unit.name}</div>
                </td>
                <td className="mono">{variance.element_reference}</td>
                <td>{monthLabel(variance.period_month)}</td>
                <td>
                  <span className={`badge ${variance.source.code === 'ACTUALS' ? 'pill-info' : 'pill-neutral'}`}>
                    {SOURCE_LABELS[variance.source.code] ?? variance.source.code}
                  </span>
                </td>
                <td className="num">{formatAmount(variance.baseline_amount, currency)}</td>
                <td className="num">{formatAmount(variance.computed_amount, currency)}</td>
                <td className="num">
                  {formatAmount(variance.incoming_amount, currency)}
                  {variance.incoming_absent && <div className="hint">not in the file</div>}
                </td>
                <td className={`num ${varianceClass(variance.variance_amount)}`}>
                  <b>{formatAmount(variance.variance_amount, currency, { signed: true })}</b>
                  <div className="hint">{currency}</div>
                </td>
                <td>
                  <span className={`badge ${statusPill(variance.status.code)}`}>
                    {STATUS_LABELS[variance.status.code] ?? variance.status.code}
                  </span>
                </td>
                <td><Outcome variance={variance} /></td>
                <td className="rc-actions">
                  {showsActions(variance, cycleStateCode) && (
                    <span className="rc-action-group">
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => onDecide(variance, 'ACCEPT')}>
                        <Check aria-hidden="true" /> Accept
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onDecide(variance, 'REJECT')}>
                        <X aria-hidden="true" /> Reject
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
